import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import { routeSteps } from "@/mocks/vivelo";
import { fetchMyQuizResult, type QuizResult } from "@/services/quiz";
import { fetchCurrentProfile } from "@/services/auth";

const stepBg: Record<string, string> = {
  primary: "bg-primary-500/15 text-primary-600",
  accent: "bg-accent-500/15 text-accent-600",
  secondary: "bg-secondary-500/15 text-secondary-600",
};

export default function Resultado() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [firstName, setFirstName] = useState("tú");

  useEffect(() => {
    let active = true;
    Promise.all([fetchMyQuizResult(), fetchCurrentProfile()])
      .then(([res, profile]) => {
        if (!active) return;
        setResult(res);
        if (profile?.fullName) setFirstName(profile.fullName.split(" ")[0]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <AppShell headerVariant="onDark" showBack>
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 text-center text-foreground-400">Cargando tu resultado...</div>
      </AppShell>
    );
  }

  if (!result) {
    return (
      <AppShell headerVariant="onDark" showBack>
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-accent-500/15 flex items-center justify-center mb-4">
            <i className="ri-brain-line text-3xl text-accent-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-white">Aún no has tomado tu test</h1>
          <p className="text-foreground-300 text-sm mt-2 max-w-sm mx-auto">
            Responde 16 preguntas rápidas y descubre tu perfil de personalidad y tu ruta de crecimiento personalizada.
          </p>
          <button
            onClick={() => navigate("/app/test")}
            className="mt-6 inline-flex items-center gap-2 vv-gradient-brand text-background-950 rounded-full px-6 py-3 font-heading font-bold text-sm cursor-pointer whitespace-nowrap"
          >
            Empezar el test <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </AppShell>
    );
  }

  const { profile } = result;

  return (
    <AppShell headerVariant="onDark" showBack>
      {/* Hero dark */}
      <section className="bg-background-950 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 pb-10 relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-40"
            style={{ background: "radial-gradient(500px 220px at 90% 20%, oklch(var(--accent-700) / 0.55), transparent 60%), radial-gradient(500px 220px at 5% 100%, oklch(var(--primary-800) / 0.5), transparent 60%)" }}></div>

          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-extrabold">
                ¡Felicidades, {firstName}! <span aria-hidden="true">🎉</span>
              </h1>
              <p className="text-foreground-200 text-sm mt-2 max-w-md">
                Has completado tu test de personalidad emprendedora. Este es tu perfil y tu ruta personalizada.
              </p>
            </div>
            <button className="inline-flex items-center gap-2 bg-background-900/60 border border-accent-700/40 rounded-full px-4 py-2.5 text-sm text-white hover:border-primary-500 cursor-pointer whitespace-nowrap">
              <i className="ri-share-forward-line"></i> Compartir resultado
            </button>
          </div>

          {/* Test completado ring + profile */}
          <div className="relative mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2 bg-background-900/60 border border-accent-700/40 rounded-2xl p-5 md:p-6 flex items-center gap-5">
              <div className="w-20 h-20 rounded-2xl bg-accent-500/15 flex items-center justify-center">
                <i className={`${profile.icon} text-4xl text-accent-300`}></i>
              </div>
              <div className="flex-1">
                <div className="text-xs text-foreground-300">Tu perfil es:</div>
                <div className="font-heading font-extrabold text-2xl md:text-3xl vv-gradient-text">{profile.label}</div>
                <span className="inline-flex items-center gap-1 mt-2 text-[11px] bg-accent-500/15 text-accent-200 px-2.5 py-1 rounded-full">
                  Temperamento base: {profile.temperament} <i className="ri-heart-line"></i>
                </span>
              </div>
            </div>
            <div className="bg-background-900/60 border border-primary-700/40 rounded-2xl p-5 flex flex-col items-center text-center">
              <div className="relative w-24 h-24">
                <div className="absolute inset-0 rounded-full border-4 border-background-800"></div>
                <div className="absolute inset-0 rounded-full border-4 border-primary-500" style={{ clipPath: "inset(0 0 0 0)" }}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-check-line text-primary-400 text-3xl"></i>
                </div>
              </div>
              <div className="mt-3 text-primary-400 text-xs font-semibold">Test completado</div>
              <div className="font-heading font-extrabold text-2xl mt-1">{result.percent}%</div>
              <div className="text-[11px] text-foreground-300">{result.totalQuestions} de {result.totalQuestions} preguntas</div>
            </div>
          </div>

          <div className="relative mt-6 bg-background-900/60 border border-accent-700/30 rounded-2xl p-5 md:p-6">
            <p className="text-foreground-200 text-sm md:text-base">{profile.description}</p>
          </div>
        </div>
      </section>

      {/* Light content */}
      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          {/* Fortalezas / Reto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-background-100 border border-background-200 rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-lg bg-primary-500/15 flex items-center justify-center">
                  <i className="ri-vip-diamond-line text-primary-600"></i>
                </span>
                <h3 className="font-heading font-bold text-foreground-950">Tus fortalezas</h3>
              </div>
              <ul className="space-y-2">
                {profile.strengths.map((s, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-foreground-800">
                    <span className="w-5 h-5 rounded-full bg-primary-500/20 text-primary-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-background-100 border border-background-200 rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <i className="ri-flag-2-line text-amber-600"></i>
                </span>
                <h3 className="font-heading font-bold text-foreground-950">Tu reto a superar</h3>
              </div>
              <ul className="space-y-2">
                {profile.challenges.map((s, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-foreground-800">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-refresh-line text-xs"></i>
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Ruta */}
          <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl p-5 md:p-6">
            <h3 className="text-center font-heading font-bold text-foreground-950 text-lg">
              Tu ruta <span className="text-primary-600">personalizada</span> de crecimiento
            </h3>
            <div className="relative mt-6">
              <div className="hidden md:block absolute left-6 right-6 top-8 h-0.5 border-t border-dashed border-background-300"></div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 relative">
                {routeSteps.map((r) => (
                  <div key={r.n} className="flex flex-col items-center text-center">
                    <span className={`w-14 h-14 rounded-full ${stepBg[r.accent]} flex items-center justify-center relative`}>
                      <i className={`${r.icon} text-2xl`}></i>
                    </span>
                    <div className="mt-2 text-sm font-heading font-bold text-foreground-950">
                      {r.n}. {r.title}
                    </div>
                    <div className="text-xs text-foreground-500">{r.subtitle}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 bg-primary-500/10 border border-primary-500/25 rounded-xl p-4 flex items-start gap-3">
              <span className="w-8 h-8 rounded-full bg-primary-500/20 text-primary-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-sparkling-2-fill"></i>
              </span>
              <p className="text-sm text-foreground-800">
                Esta ruta ha sido diseñada según tu perfil, tus respuestas y tus objetivos.
                <br />
                <span className="font-semibold text-foreground-950">Sigue este camino y alcanzarás resultados extraordinarios.</span>
              </p>
            </div>
          </div>

          {/* Next step */}
          <div className="mt-6 bg-background-950 text-white rounded-2xl p-5 md:p-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-70 pointer-events-none"
              style={{ background: "radial-gradient(600px 220px at 20% 30%, oklch(var(--accent-700) / 0.5), transparent 60%)" }}></div>
            <div className="relative flex items-center gap-2 text-primary-300 text-xs font-semibold">
              <i className="ri-sparkling-2-fill"></i> Tu próximo paso recomendado
            </div>
            <div className="relative mt-3 flex flex-col md:flex-row md:items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-background-900 border border-primary-500/40 flex items-center justify-center relative">
                <i className="ri-play-fill text-3xl text-primary-400"></i>
                <span className="absolute -bottom-2 text-[10px] font-mono bg-background-950 px-2 py-0.5 rounded-full border border-primary-500/50 text-primary-300">
                  12:45
                </span>
              </div>
              <div className="flex-1">
                <div className="font-heading font-bold text-lg">Masterclass para tu perfil</div>
                <p className="text-sm text-foreground-300 mt-1">
                  Descubre cómo personas con tu perfil están creando libertad financiera y ayudando a otros.
                </p>
              </div>
              <button
                onClick={() => navigate("/app/cursos")}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-950 rounded-full px-5 py-2.5 font-heading font-bold text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                Ver masterclass <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/app/mi-ruta")}
              className="w-full py-3.5 rounded-xl bg-background-100 border border-background-300 text-foreground-950 font-heading font-semibold inline-flex items-center justify-center gap-2 hover:border-primary-400 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-2-line"></i> Ver mi ruta completa
            </button>
            <button
              onClick={() => navigate("/app/dashboard")}
              className="w-full py-3.5 rounded-xl vv-gradient-brand text-background-950 font-heading font-bold inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Ir a mi dashboard <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>
    </AppShell>
  );
}
